// Client Portal panel (deal-detail right column) — the agent-facing controls
// for the client portal: one magic link the client uses to fill out every
// portal form for their side of the deal (utility sheet, all-about-you, buyer
// preferences, …).
//
// Reads two things off the deal doc:
//   deal.clientPortal — the active link summary written by /portal/issue
//                       ({ token, url, contactId, contactName, createdAt,
//                       expiresAt, revoked })
//   deal.portalForms  — map of formId -> { answers, submittedAt, pdfDriveFileId? },
//                       one entry per form the client has started or submitted
//
// After issue/revoke the parent is told via onPortalChange(nextClientPortal)
// (same optimistic-patch idiom as ClientInform's onCustomersChange) — the
// backend has already persisted the same summary to the deal doc.
import { useEffect, useMemo, useState } from "react";
import { Copy, ExternalLink, Link2, Mail, Search, XCircle } from "lucide-react";
import { toast } from "react-toastify";
import { useStateValue } from "../../context/StateProvider";
import { useActionDialog } from "../ActionDialog";
import {
  issuePortal,
  revokePortal,
  IS_DEV,
  makeDemoPortal,
  friendlyPortalError,
} from "../../utils/clientPortal";
import { carryOverPortalAnswers } from "../../utils/portalAnswerCarryover";
import {
  formsForRequest,
  getForm,
  applyOverrides,
  applyOverridesAll,
  isFieldVisible,
} from "../../utils/portalForms";
import PortalFormAnswersModal, {
  isEmptyAnswer,
} from "../PortalFormAnswersModal";
import { loadPortalFormOverrides } from "../../utils/portalFormOverrides";
import {
  getContactsForUser,
  getContactsByIds,
} from "../../utils/firebasefunctions";
import { sendTransactionEmail } from "../../utils/emailSender";
import {
  plainTextToHtml,
  wrapBrandedEmail,
} from "../../utils/emailBrandTemplate";
import { buildButton } from "../../utils/openHouseEmailsHtml";
import { emailsForContacts, splitToBcc } from "../../utils/emailFanout";
import PortalRecipientsModal from "../email/PortalRecipientsModal";
import { recordSentEmail } from "../../utils/sentEmails";
import EmailComposer from "../email/EmailComposer";

// Contact-record name fields vary (app-saved vs legacy vs FUB import).
const pickName = (c) =>
  c?.name ||
  c?.clientName ||
  c?.fullName ||
  [c?.firstName, c?.lastName].filter(Boolean).join(" ") ||
  "";

const pickEmail = (c) => c?.email || c?.clientEmail || "";

// Mirrors the backend's /portal/issue rule: the portal gate asks the client
// for their LAST name, so the contact needs one we can resolve. A single-word
// name equal to firstName is a first-name-only contact (Google sync writes
// lastName:"") — that would make the gate unpassable, so it doesn't count.
const pickLastName = (c) => {
  const explicit = String(c?.lastName || "").trim();
  if (explicit) return explicit;
  const firstNorm = String(c?.firstName || "").trim().toLowerCase();
  for (const v of [c?.name, c?.clientName, c?.fullName]) {
    const words = String(v || "").trim().split(/\s+/).filter(Boolean);
    if (words.length >= 2) return words[words.length - 1];
    if (words.length === 1 && words[0].toLowerCase() !== firstNorm)
      return words[0];
  }
  return "";
};

// createdAt/expiresAt may be millis or ISO strings — Date() handles both.
const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const smallBtn =
  "inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 border border-slate-300 hover:border-slate-500 rounded-md px-2.5 py-1.5 transition";

function ClientPortalPanel({ deal, onPortalChange }) {
  const [{ user }] = useStateValue();
  const { confirm, dialog } = useActionDialog();
  const dealId = String(deal?.id || deal?.createdat || "");
  const portal = deal?.clientPortal;

  const portalForms = deal?.portalForms || {};

  const expired =
    !!portal?.expiresAt && new Date(portal.expiresAt).getTime() < Date.now();
  const linkActive = !!(portal?.token && portal?.url && !portal.revoked && !expired);

  // --- contact selection (create-link state) ---
  // Modern deals store sync_contacts ids in deal.customers; legacy deals store
  // inline { clientName, ... } objects (no contact id → fall back to search).
  const customerIds = useMemo(
    () =>
      (deal?.customers || []).filter(
        (c) => typeof c === "string" && c.trim() !== "",
      ),
    [deal?.customers],
  );
  const legacyName = useMemo(() => {
    const legacy = (deal?.customers || []).find(
      (c) => c && typeof c === "object",
    );
    return pickName(legacy);
  }, [deal?.customers]);

  const [dealContacts, setDealContacts] = useState([]);
  const [selectedContactId, setSelectedContactId] = useState("");
  const [pickedContact, setPickedContact] = useState(null); // search fallback pick
  const [searchTerm, setSearchTerm] = useState("");
  const [allContacts, setAllContacts] = useState(null); // null = not loaded yet
  const [portalContact, setPortalContact] = useState(null); // for Send by email

  const [issuing, setIssuing] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendRecipients, setSendRecipients] = useState(null); // recipient picker
  const [activeFormId, setActiveFormId] = useState(null); // formId whose answers modal is open
  const [composingInfoTransfer, setComposingInfoTransfer] = useState(false); // Info Transfer email composer open
  // Admin form overrides (portalConfig/formOverrides) so the agent preview
  // matches what the client sees. {} until loaded and on any failure — the
  // panel then shows the base forms exactly as before.
  const [formOverrides, setFormOverrides] = useState({});

  // Slice-3 assembly: the client fills one "All About You" per client, saved
  // under the instance id "allAboutYou:{contactId}". Resolve the deal's clients
  // the SAME way the backend does (portalDealClients: seed link contact, deal
  // contactId, coClients, customers) so the panel's instance ids line up with
  // what the portal actually writes; also fold in any allAboutYou:* keys already
  // present in portalForms so saved answers are never hidden.
  const clients = useMemo(() => {
    const ids = [];
    const push = (v) => {
      const id = String(v || "").trim();
      if (id && !ids.includes(id)) ids.push(id);
    };
    push(portal?.contactId);
    push(deal?.contactId);
    (deal?.coClients || []).forEach((c) => push(c && (c.contactId || c.id)));
    (deal?.customers || []).forEach((c) =>
      push(typeof c === "string" ? c : c && (c.contactId || c.id)),
    );
    Object.keys(portalForms).forEach((k) => {
      if (k.startsWith("allAboutYou:")) push(k.slice("allAboutYou:".length));
    });
    return ids.map((id) => {
      const c = dealContacts.find((x) => String(x.id) === id);
      return {
        contactId: id,
        name: c ? pickName(c) : "",
        firstName: c?.firstName || "",
      };
    });
  }, [
    portal?.contactId,
    deal?.contactId,
    deal?.coClients,
    deal?.customers,
    dealContacts,
    portalForms,
  ]);

  // Every form this deal should show, assembled the way the backend/portal do:
  // "All About You" expands one-per-client (instance ids), and the property-type
  // sections (Association/Pool/Multi-Family/Unit) appear. With no resolvable
  // clients (legacy inline-only deal), fall back to the side-based list so the
  // base forms still preview.
  const forms = useMemo(
    () =>
      applyOverridesAll(
        // Always assemble by formType (honor the property-type sections). With
        // no resolvable clients (still loading, or a legacy inline-only deal),
        // formsForRequest simply omits the per-client "All About You" instances
        // rather than falling back to the whole side list — which would ignore
        // formType and show every section.
        formsForRequest({
          formType: deal?.formType,
          side: deal?.dealtype,
          clients,
        }),
        formOverrides,
      ),
    [clients, deal?.formType, deal?.dealtype, formOverrides],
  );
  const submittedCount = forms.filter(
    (f) => !!portalForms[f.id]?.submittedAt,
  ).length;
  // Read-only forms (e.g. Video Library) have no Submit, so exclude them from
  // the denominator — otherwise "X of N" can never reach N.
  const submittableCount = forms.filter(
    (f) => !f.fields.every((x) => x.type === "video"),
  ).length;

  // Load the admin form overrides once so the preview matches the client's view.
  // loadPortalFormOverrides never throws (returns {} on missing/error).
  useEffect(() => {
    let alive = true;
    loadPortalFormOverrides().then((ov) => {
      if (alive) setFormOverrides(ov || {});
    });
    return () => {
      alive = false;
    };
  }, []);

  // Resolve the deal's contact ids (same pattern as ClientInform).
  useEffect(() => {
    let alive = true;
    if (customerIds.length === 0) {
      setDealContacts([]);
      return;
    }
    getContactsByIds(customerIds).then((contacts) => {
      if (alive) setDealContacts(contacts || []);
    });
    return () => {
      alive = false;
    };
  }, [customerIds]);

  // Pre-select the first deal contact the portal can actually work for
  // (email + a resolvable last name), degrading gracefully from there.
  useEffect(() => {
    if (dealContacts.length === 0) return;
    setSelectedContactId((prev) => {
      if (prev && dealContacts.some((c) => String(c.id) === prev)) return prev;
      const best =
        dealContacts.find((c) => pickEmail(c) && pickLastName(c)) ||
        dealContacts.find((c) => pickLastName(c)) ||
        dealContacts.find((c) => pickEmail(c)) ||
        dealContacts[0];
      return String(best.id);
    });
  }, [dealContacts]);

  // Legacy fallback: seed the search box with the inline client name and lazily
  // load the user's contact list (cached 60s inside the util).
  const needsSearch = !linkActive && customerIds.length === 0;
  useEffect(() => {
    if (needsSearch) setSearchTerm((prev) => prev || legacyName || "");
  }, [needsSearch, legacyName]);
  useEffect(() => {
    let alive = true;
    if (!needsSearch || allContacts !== null || !user?.id) return;
    getContactsForUser(user)
      .then((list) => {
        if (alive) setAllContacts(list || []);
      })
      .catch((err) => console.error("Failed to load contacts:", err));
    return () => {
      alive = false;
    };
  }, [needsSearch, allContacts, user?.id, user]);

  const searchMatches = useMemo(() => {
    if (!needsSearch || pickedContact || !allContacts) return [];
    const q = searchTerm.trim().toLowerCase();
    if (!q) return [];
    return allContacts
      .filter(
        (c) =>
          pickName(c).toLowerCase().includes(q) ||
          pickEmail(c).toLowerCase().includes(q),
      )
      .slice(0, 6);
  }, [needsSearch, pickedContact, allContacts, searchTerm]);

  const selectedContact =
    dealContacts.find((c) => String(c.id) === selectedContactId) ||
    pickedContact;

  // Resolve the active link's contact so "Send by email" knows the address.
  useEffect(() => {
    let alive = true;
    if (!linkActive || !portal?.contactId) {
      setPortalContact(null);
      return;
    }
    const local = dealContacts.find(
      (c) => String(c.id) === String(portal.contactId),
    );
    if (local) {
      setPortalContact(local);
      return;
    }
    getContactsByIds([portal.contactId]).then((r) => {
      if (alive) setPortalContact(r?.[0] || null);
    });
    return () => {
      alive = false;
    };
  }, [linkActive, portal?.contactId, dealContacts]);

  // --- actions ---
  const handleCreate = async () => {
    if (!selectedContactId) {
      toast.error("Pick a client contact first");
      return;
    }
    if (selectedContact && !pickLastName(selectedContact)) {
      // The backend would 400 anyway — explain it here so the agent knows
      // exactly what to fix instead of retrying a doomed link.
      toast.error(
        "This contact has no last name on file — the portal's identity check needs one. Add it in Contacts first.",
      );
      return;
    }
    setIssuing(true);
    try {
      const res = await issuePortal({ dealId, contactId: selectedContactId });
      // Sandbox has no issue endpoint deployed — degrade to a local demo link
      // (mirrors the seller page's devLocal fallback: try the backend first,
      // fall back only if it isn't there).
      const issued = res.ok || !IS_DEV ? res : makeDemoPortal(dealId);
      if (!issued.ok) {
        toast.error(friendlyPortalError(res));
        return;
      }
      // Backend wrote the same summary to deals/{id}.clientPortal — mirror it
      // locally so the panel flips to the active-link state immediately.
      onPortalChange?.({
        token: issued.token,
        url: issued.url,
        contactId: selectedContactId,
        contactName: pickName(selectedContact) || "",
        createdAt: Date.now(),
        expiresAt: issued.expiresAt,
        revoked: false,
      });
      toast.success(
        issued.demo ? "Portal link created (demo)" : "Portal link created",
      );
      // A double client (selling AND buying) already answered All About You
      // on their other file — copy it in so their forms open pre-filled.
      // Best-effort; never blocks or fails the link.
      const carried = await carryOverPortalAnswers(dealId);
      if (carried.copied?.length) {
        toast.success(
          "Copied their All About You answers from their other file.",
        );
      }
    } finally {
      setIssuing(false);
    }
  };

  const handleRevoke = async () => {
    const ok = await confirm({
      title: "Revoke portal link",
      body: "Revoke this portal link?",
      note: "The client will no longer be able to open it.",
      tone: "danger",
      confirmLabel: "Revoke",
    });
    if (!ok) return;
    setRevoking(true);
    try {
      const res = await revokePortal({ token: portal.token });
      // In the sandbox the revoke endpoint isn't deployed — treat it as a local
      // success so the demo flow is exercisable end to end.
      if (!res.ok && !IS_DEV) {
        toast.error(friendlyPortalError(res));
        return;
      }
      onPortalChange?.({ ...portal, revoked: true });
      toast.success("Portal link revoked");
    } finally {
      setRevoking(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(portal.url);
      toast.success("Link copied");
    } catch (err) {
      console.error("Clipboard write failed:", err);
      toast.error("Copy failed — select the link text instead");
    }
  };

  // The portal link goes to EVERY client on the file who has an email (the
  // whole household, not just the one it was issued to), first as `to` and the
  // rest bcc'd. Fall back to the link's own contact for a legacy inline deal.
  const recipientPool = dealContacts.length
    ? dealContacts
    : portalContact
      ? [portalContact]
      : [];
  const recipientEmails = emailsForContacts(recipientPool);
  const portalEmail = recipientEmails[0] || "";
  // "Send by email" opens the recipient picker (everyone pre-checked); doSendEmail
  // fires once the agent confirms who it goes to.
  const handleSendEmail = () => {
    if (!recipientEmails.length) {
      toast.error("No email on file for anyone on this file");
      return;
    }
    setSendRecipients(
      recipientPool.map((c) => ({
        contactId: c.id,
        name: pickName(c),
        email: pickEmail(c),
      })),
    );
  };
  const doSendEmail = async (selected) => {
    // The modal returns recipient OBJECTS; this panel emails only.
    const selectedEmails = (Array.isArray(selected) ? selected : [])
      .map((r) => (typeof r === "string" ? r : r?.email))
      .filter(Boolean);
    if (!selectedEmails.length) return;
    // Demo mode: the sandbox link is synthetic, so skip the real send (which in
    // the sandbox would fire a genuine email at a fake token) and report a local
    // success — the agent can still see the whole flow.
    if (IS_DEV) {
      toast.success("Email sent (demo — nothing was actually sent)");
      setSendRecipients(null);
      return;
    }
    setSendingEmail(true);
    try {
      const { to, bcc, all } = splitToBcc(selectedEmails);
      const toName = portal.contactName || pickName(portalContact) || "";
      const firstName = toName.split(" ")[0] || "there";
      const address = deal?.propertyaddress || "";
      const subject = address
        ? `Utility Information Sheet for ${address}`
        : "Your Utility Information Sheet";
      const intro =
        `Hi ${firstName},\n\n` +
        `As part of getting ${address || "your home"} ready, we've set up a quick Utility Information Sheet for you: utility companies, school info, and the ages of the major systems. It only takes a few minutes, and your answers save as you go.`;
      const outro = `Thank you!\nTeam Edelstein`;
      // Plain-text fallback keeps the bare URL; the HTML body gets a real
      // <a href> button — Outlook and friends don't auto-link bare URLs.
      const text = `${intro}\n\nOpen your secure link here:\n${portal.url}\n\n${outro}`;
      const html = wrapBrandedEmail({
        contentHtml:
          plainTextToHtml(intro) +
          `<p style="margin:18px 0;">${buildButton(portal.url, "Open your Utility Info Sheet")}</p>` +
          plainTextToHtml(outro),
        sender: user,
        propertyAddress: address,
        videoCard: "",
      });
      const res = await sendTransactionEmail({
        userId: user?.id,
        to,
        toName,
        bcc,
        subject,
        text,
        html,
        emailId: "client-portal-link",
        relatedDealId: dealId,
      });
      if (!res?.ok) {
        toast.error(`Send failed: ${res?.error || "unknown error"}`);
        return;
      }
      setSendRecipients(null);
      try {
        await recordSentEmail({
          dealId,
          dealAddress: address,
          emailId: "client-portal-link",
          sequenceSide: deal?.dealtype || "",
          agentId: user?.id || null,
          agentName: user?.displayname || "",
          recipientEmail: all.join(", "),
          recipientName: toName,
          subject,
          bodyHtml: html,
          attachments: [],
          videoUrl: "",
          gmailMessageId: res.gmailMessageId || null,
        });
      } catch (e) {
        console.error("History write failed (email DID send):", e);
      }
      toast.success(
        selectedEmails.length > 1
          ? `Email sent to ${selectedEmails.length} clients`
          : "Email Sent",
      );
    } finally {
      setSendingEmail(false);
    }
  };

  // Prefer the assembled instance (name-titled, e.g. "All About You — Jane");
  // fall back to the base registry form for any id not in the assembled list.
  const activeForm = activeFormId
    ? forms.find((f) => f.id === activeFormId) ||
      applyOverrides(
        getForm(activeFormId),
        formOverrides[String(activeFormId || "").split(":")[0]],
      )
    : null;
  const activeEntry = activeFormId ? portalForms[activeFormId] : null;

  // "Send Information Transfer" — once the client has submitted the Information
  // Transfer form and its PDF has been generated, staff can email that PDF out
  // via the standard EmailComposer (they pick the recipient + send).
  const infoTransferEntry = portalForms.informationTransfer;
  const canSendInfoTransfer = !!(
    infoTransferEntry?.submittedAt && infoTransferEntry?.pdfDriveFileId
  );
  const infoTransferRecipient = useMemo(() => {
    const c =
      portalContact ||
      selectedContact ||
      dealContacts.find((x) => pickEmail(x));
    return {
      name: pickName(c) || portal?.contactName || "",
      email: pickEmail(c),
    };
  }, [portalContact, selectedContact, dealContacts, portal?.contactName]);
  const infoTransferEmail = useMemo(() => {
    const address = deal?.propertyaddress || "";
    const firstName = (infoTransferRecipient.name.split(" ")[0] || "there").trim();
    return {
      id: "information-transfer",
      subject: address
        ? `Information Transfer for ${address}`
        : "Information Transfer",
      body:
        `Hi ${firstName},\n\n` +
        `Attached is the completed Information Transfer sheet${
          address ? ` for ${address}` : ""
        }. It covers the key details (utilities, systems, and access) to pass along at closing. Let us know if anything needs updating.\n\n` +
        `Thank you!\nTeam Edelstein`,
      recipientLabel: "Client",
      recipientRole: "client",
    };
  }, [deal?.propertyaddress, infoTransferRecipient.name]);
  const infoTransferAttachments = useMemo(
    () =>
      infoTransferEntry?.pdfDriveFileId
        ? [
            {
              name: "Information Transfer.pdf",
              source: "drive",
              fileId: infoTransferEntry.pdfDriveFileId,
            },
          ]
        : [],
    [infoTransferEntry?.pdfDriveFileId],
  );

  return (
    <div className="bg-white rounded-xl shadow p-6">
      <div className="border-b border-black/10 pb-3">
        <h3 className="text-lg font-semibold">Client Portal</h3>
      </div>

      <div className="space-y-3 mt-3">
        {/* Per-form progress */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {submittedCount} of {submittableCount} submitted
          </p>
          <div className="mt-2 space-y-1.5">
            {forms.map((form) => {
              const entry = portalForms[form.id];
              const isSubmitted = !!entry?.submittedAt;
              const started =
                !isSubmitted &&
                form.fields.some(
                  (f) =>
                    isFieldVisible(f, entry?.answers || {}) &&
                    !isEmptyAnswer(f, entry?.answers?.[f.key]),
                );
              const hasAnswers = isSubmitted || started;
              return (
                <div
                  key={form.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setActiveFormId(form.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setActiveFormId(form.id);
                    }
                  }}
                  title="Preview this form's questions"
                  className="flex items-center justify-between gap-2 p-2.5 rounded-lg bg-gray-50 hover:bg-gray-100 cursor-pointer transition"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">
                      {form.title}
                    </p>
                    {isSubmitted ? (
                      <span className="inline-flex items-center gap-1 mt-1 text-xs font-semibold text-green-700 bg-green-100 rounded-full px-2 py-0.5">
                        ✓ Submitted {fmtDate(entry.submittedAt)}
                      </span>
                    ) : started ? (
                      <span className="inline-flex items-center gap-1 mt-1 text-xs font-semibold text-amber-700 bg-amber-100 rounded-full px-2 py-0.5">
                        In progress
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 mt-1 text-xs font-semibold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">
                        Not started
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveFormId(form.id);
                      }}
                      className={smallBtn}
                    >
                      {hasAnswers ? "View answers" : "Preview"}
                    </button>
                    {entry?.pdfDriveFileId && (
                      <a
                        href={`https://drive.google.com/file/d/${entry.pdfDriveFileId}/view`}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className={smallBtn}
                      >
                        <ExternalLink size={12} /> Open PDF
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {canSendInfoTransfer && (
          <button
            type="button"
            onClick={() => setComposingInfoTransfer(true)}
            className="w-full inline-flex items-center justify-center gap-2 text-sm font-semibold text-slate-700 border border-slate-300 hover:border-slate-500 rounded-md px-3 py-2 transition"
          >
            <Mail size={14} /> Send Information Transfer
          </button>
        )}

        {linkActive ? (
          /* ---- active link ---- */
          <div className="p-3 rounded-lg bg-gray-50 space-y-2">
            <div>
              <p className="text-sm text-gray-500">Portal link sent to</p>
              <p className="font-semibold">
                {portal.contactName || pickName(portalContact) || "Client"}
              </p>
            </div>
            <div className="flex gap-4 text-xs text-slate-500">
              <span>Created {fmtDate(portal.createdAt)}</span>
              <span>Expires {fmtDate(portal.expiresAt)}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <input
                readOnly
                value={portal.url}
                onFocus={(e) => e.target.select()}
                className="flex-1 min-w-0 text-xs text-slate-600 bg-white border border-slate-200 rounded-md px-2 py-1.5"
              />
              <button
                type="button"
                onClick={handleCopy}
                className={smallBtn}
                aria-label="Copy portal link"
              >
                <Copy size={12} /> Copy
              </button>
              <a
                href={portal.url}
                target="_blank"
                rel="noreferrer"
                className={smallBtn}
                aria-label="Open portal link"
              >
                <ExternalLink size={12} /> Open
              </a>
            </div>
            <div className="flex items-center gap-1.5 pt-1">
              <button
                type="button"
                onClick={handleSendEmail}
                disabled={!portalEmail || sendingEmail}
                className={`${smallBtn} disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <Mail size={12} />
                {sendingEmail
                  ? "Sending…"
                  : recipientEmails.length > 1
                    ? `Send by email (${recipientEmails.length})`
                    : "Send by email"}
              </button>
              <button
                type="button"
                onClick={handleRevoke}
                disabled={revoking}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-600 border border-red-200 hover:border-red-400 hover:bg-red-50 rounded-md px-2.5 py-1.5 transition disabled:opacity-50"
              >
                <XCircle size={12} />
                {revoking ? "Revoking…" : "Revoke"}
              </button>
            </div>
            {recipientEmails.length > 1 && (
              <p className="text-xs text-slate-400">
                Sends to all {recipientEmails.length} clients on the file.
              </p>
            )}
            {!portalEmail && (
              <p className="text-xs text-slate-400">
                No email on file for anyone on this file — copy the link instead.
              </p>
            )}
          </div>
        ) : (
          /* ---- no active link: pick a contact + create ---- */
          <div className="space-y-2">
            {portal?.revoked && (
              <p className="text-xs text-slate-400">
                The previous link was revoked. Create a new one below.
              </p>
            )}
            {!portal?.revoked && expired && (
              <p className="text-xs text-slate-400">
                The previous link expired {fmtDate(portal.expiresAt)}. Create a
                new one below.
              </p>
            )}

            <p className="text-sm text-gray-500">Send to</p>
            {dealContacts.length > 0 ? (
              <select
                value={selectedContactId}
                onChange={(e) => setSelectedContactId(e.target.value)}
                className="w-full text-sm bg-white border border-slate-300 rounded-md px-2.5 py-2 outline-none focus:border-slate-500 transition"
              >
                {dealContacts.map((c) => (
                  <option key={c.id} value={String(c.id)}>
                    {pickName(c) || "(no name)"}
                    {pickEmail(c) ? ` — ${pickEmail(c)}` : ""}
                    {pickLastName(c) ? "" : " (no last name)"}
                  </option>
                ))}
              </select>
            ) : (
              <div className="relative">
                <Search
                  size={13}
                  className="absolute left-2.5 top-[15px] text-slate-400 pointer-events-none"
                />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPickedContact(null);
                    setSelectedContactId("");
                  }}
                  placeholder="Search your contacts…"
                  className="w-full bg-slate-50 border border-slate-200 focus:bg-white focus:border-slate-400 rounded-md pl-8 pr-3 py-2 text-sm outline-none transition"
                />
                {searchMatches.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-md shadow max-h-48 overflow-y-auto">
                    {searchMatches.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => {
                          setPickedContact(c);
                          setSelectedContactId(String(c.id));
                          setSearchTerm(pickName(c) || "(no name)");
                        }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
                      >
                        <span className="font-medium text-slate-900">
                          {pickName(c) || "(no name)"}
                        </span>
                        {pickEmail(c) && (
                          <span className="text-slate-400">
                            {" "}
                            — {pickEmail(c)}
                          </span>
                        )}
                        {c._teamOwnerName && (
                          <span className="ml-1 text-xs text-slate-400">
                            · {c._teamOwnerName}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
                {allContacts !== null &&
                  searchTerm.trim() !== "" &&
                  !pickedContact &&
                  searchMatches.length === 0 && (
                    <p className="text-xs text-slate-400 mt-1">
                      No contacts match "{searchTerm.trim()}". Add the client
                      in Client Information first.
                    </p>
                  )}
              </div>
            )}

            <button
              type="button"
              onClick={handleCreate}
              disabled={!selectedContactId || issuing}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#1E293B] hover:opacity-90 text-white rounded-md px-3 py-2 text-sm font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Link2 size={14} />
              {issuing ? "Creating…" : "Create portal link"}
            </button>
          </div>
        )}
      </div>

      {/* View-answers modal — shared with the lead drawers */}
      {activeForm && (
        <PortalFormAnswersModal
          form={activeForm}
          entry={activeEntry}
          onClose={() => setActiveFormId(null)}
        />
      )}

      {/* Send Information Transfer — opens the standard EmailComposer with the
          completed PDF pre-attached; staff pick the recipient and send. */}
      {composingInfoTransfer && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setComposingInfoTransfer(false)}
        >
          <div
            className="w-full max-w-5xl max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <EmailComposer
              deal={deal}
              contacts={dealContacts}
              sender={user}
              email={infoTransferEmail}
              defaultRecipient={infoTransferRecipient}
              initialAttachments={infoTransferAttachments}
              onSent={() => {
                setComposingInfoTransfer(false);
                toast.success("Information Transfer sent");
              }}
              onCancel={() => setComposingInfoTransfer(false)}
            />
          </div>
        </div>
      )}

      {sendRecipients && (
        <PortalRecipientsModal
          title="Send portal link"
          recipients={sendRecipients}
          busy={sendingEmail}
          allowText={false}
          onCancel={() => setSendRecipients(null)}
          onSend={doSendEmail}
        />
      )}
      {dialog}
    </div>
  );
}

export default ClientPortalPanel;
